import React, { useState } from 'react';
import { Menu, Bell, LogOut, User } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

interface HeaderProps {
  onMenuClick: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
  };

  return (
    <header className="bg-white shadow-soft border-b border-gray-200/60 backdrop-blur-sm">
      <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
        {/* Mobile menu button */}
        <button
          onClick={onMenuClick}
          className="lg:hidden p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200 hover:scale-105"
        >
          <Menu className="h-6 w-6" />
        </button>

        {/* Page title - will be dynamic based on route */}
        <div className="hidden lg:block">
          <h1 className="text-2xl font-bold gradient-text">
            {getPageTitle(window.location.pathname)}
          </h1>
        </div>

        {/* Right side items */}
        <div className="flex items-center space-x-4">
          {/* Notifications */}
          <button className="p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded-md">
            <Bell className="h-5 w-5" />
          </button>

          {/* User menu */}
          <div className="relative">
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="flex items-center space-x-3 p-2 text-sm rounded-md text-gray-700 hover:bg-gray-100"
            >
              <div className="h-8 w-8 bg-primary-600 rounded-full flex items-center justify-center">
                <User className="h-4 w-4 text-white" />
              </div>
              <span className="hidden md:block font-medium">{user?.username}</span>
            </button>

            {/* User dropdown menu */}
            {isUserMenuOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                <div className="py-1">
                  <div className="px-4 py-2 text-sm text-gray-700 border-b border-gray-200">
                    <div className="font-medium">{user?.username}</div>
                    <div className="text-gray-500">{user?.email}</div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <LogOut className="h-4 w-4 mr-3" />
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

// Helper function to get page title based on pathname
const getPageTitle = (pathname: string): string => {
  const routes: { [key: string]: string } = {
    '/dashboard': 'Dashboard',
    '/orders': 'Orders',
    '/orders/create': 'Create Order',
    '/leads': 'Leads',
    '/technicians': 'Technicians',
    '/products': 'Products',
    '/content-creator': 'Content Creator',
  };

  // Check for edit routes
  if (pathname.startsWith('/orders/edit/')) {
    return 'Edit Order';
  }

  return routes[pathname] || 'Admin Portal';
};

export default Header; 